import { useEffect, useState } from 'react';
import { LoaderCircle, Lock, AlertTriangle } from 'lucide-react';
import { retrieveAccessTokenFromBackend } from '../../securityService';
import { API } from '../../apiUrls';
import './AuthGate.css';

type AuthGateProps = {
  children: React.ReactNode;
};

export function AuthGate({ children }: AuthGateProps) {
  const [status, setStatus] = useState<
    'checking' | 'ready' | 'forbidden' | 'error'
  >('checking');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const token = await retrieveAccessTokenFromBackend();

        if (cancelled) return;

        if (token) {
          // Token presence indicates an authenticated session.
          // The actual value is managed internally by apiClient.
          setStatus('ready');
        } else {
          redirectToLogin();
        }
      } catch (err: unknown) {
        if (cancelled) return;

        const statusCode = (err as { response?: { status?: number } })?.response?.status;

        if (statusCode === 401) {
          // Not authenticated – send user to backend login.
          redirectToLogin();
        } else if (statusCode === 403) {
          // User is authenticated but not permitted; avoid redirect loop.
          setStatus('forbidden');
        } else {
          // For other errors, stop redirecting and show a generic error.
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="auth-gate" aria-live="polite" aria-busy>
        <div className="auth-card checking" role="status">
          <div className="auth-icon">
            <LoaderCircle className="spin" strokeWidth={1.5} />
          </div>
          <div className="auth-title">Retrieving your session</div>
          <div className="auth-subtitle">Hang tight while we sign you in…</div>
        </div>
      </div>
    );
  }

  if (status === 'forbidden') {
    return (
      <div className="auth-gate">
        <div className="auth-card forbidden" role="alert">
          <div className="auth-icon">
            <Lock strokeWidth={1.5} />
          </div>
          <div className="auth-title">You don&apos;t have access</div>
          <div className="auth-subtitle">
            You&apos;re signed in, but this account isn&apos;t permitted to use Chiefy.
          </div>
          <div className="auth-actions">
            <button type="button" className="auth-btn-primary" onClick={() => redirectToLogin()}>
              Sign in with a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="auth-gate">
        <div className="auth-card error" role="alert">
          <div className="auth-icon">
            <AlertTriangle strokeWidth={1.5} />
          </div>
          <div className="auth-title">Something went wrong</div>
          <div className="auth-subtitle">
            We couldn&apos;t verify your session. Please try again.
          </div>
          <div className="auth-actions">
            <button type="button" className="auth-btn-primary" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <button type="button" className="auth-btn-secondary" onClick={() => redirectToLogin()}>
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function redirectToLogin() {
  window.location.href = API.auth.login(window.location.href);
}